import React, { useEffect, useState } from 'react';
import { Cloud, Sunset, Flower2, TreeDeciduous, ChevronDown, MapPin, ArrowRight } from 'lucide-react';
import BotanicalIcon from './BotanicalIcon';
import { BLUE_SKY_TYPES, FLOWER_TYPES, PLANT_TYPES, SUNSET_TYPES } from '../constants';

type OpenPanel = 'blue' | 'sunset' | 'flowers' | 'plants' | null;

const AUTOPLAY_ORDER: OpenPanel[] = ['blue', 'sunset', 'flowers', 'plants'];
const AUTOPLAY_MS = 2600;

function skyFanVars(
  index: number,
  count: number,
  { step = 50, arch = 36, maxAngle = 16, delayStep = 40 }: { step?: number; arch?: number; maxAngle?: number; delayStep?: number } = {},
) {
  const mid = (count - 1) / 2;
  const t = mid === 0 ? 0 : (index - mid) / mid;
  const angleDeg = t * maxAngle;
  return {
    ['--fan-x' as string]: `${((index - mid) * step).toFixed(1)}px`,
    ['--fan-y' as string]: `${(arch * (t * t - 1)).toFixed(1)}px`,
    ['--fan-rot' as string]: `${angleDeg.toFixed(2)}deg`,
    ['--fan-rot-neg' as string]: `${(-angleDeg).toFixed(2)}deg`,
    ['--fan-delay' as string]: `${index * delayStep}ms`,
    zIndex: Math.round(10 - Math.abs(index - mid)),
  };
}

function useDelayedOpen(open: boolean, delay = 80) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const id = window.setTimeout(() => setReady(true), delay);
    return () => window.clearTimeout(id);
  }, [open, delay]);
  return ready;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function BotanicalGrid({
  types,
  selectedId,
  onSelect,
  accent,
  open,
}: {
  types: { id: string; label: string; swatch: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  accent: 'pink' | 'emerald';
  open: boolean;
}) {
  const animate = useDelayedOpen(open, 90);
  return (
    <div className={`botanical-grid ${animate ? 'botanical-grid-open' : ''}`}>
      {types.map((type, index) => (
        <button
          key={type.id}
          type="button"
          title={type.label}
          onClick={() => onSelect(type.id)}
          className={`botanical-swatch botanical-swatch-${accent} ${selectedId === type.id ? 'botanical-swatch-selected' : ''}`}
          style={{ ['--swatch-delay' as string]: `${index * 28}ms` }}
        >
          <span className="botanical-disc" style={{ background: type.swatch }}>
            <BotanicalIcon id={type.id} />
          </span>
        </button>
      ))}
    </div>
  );
}

export function StylePickerShowcase({
  autoPlay = false,
  className = '',
}: {
  autoPlay?: boolean;
  className?: string;
}) {
  const [openPanel, setOpenPanel] = useState<OpenPanel>(autoPlay ? 'blue' : null);
  const [paused, setPaused] = useState(!autoPlay);
  const [flowerId, setFlowerId] = useState(FLOWER_TYPES[0].id);
  const [plantId, setPlantId] = useState(PLANT_TYPES[0].id);
  const blueReady = useDelayedOpen(openPanel === 'blue', 70);
  const sunsetReady = useDelayedOpen(openPanel === 'sunset', 70);

  useEffect(() => {
    if (!autoPlay || paused || prefersReducedMotion()) return;
    const id = window.setInterval(() => {
      setOpenPanel((current) => {
        const index = AUTOPLAY_ORDER.indexOf(current);
        return AUTOPLAY_ORDER[(index + 1) % AUTOPLAY_ORDER.length];
      });
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [autoPlay, paused]);

  const toggle = (panel: OpenPanel) => {
    setPaused(true);
    setOpenPanel((current) => (current === panel ? null : panel));
  };

  return (
    <div className={`flex flex-col lg:flex-row flex-wrap justify-center items-start gap-4 lg:gap-6 ${className}`}>
      <div className="w-full max-w-[380px] flex flex-col gap-4 overflow-visible mx-auto lg:mx-0">
        <div
          data-sky-fan
          className={`rounded-xl border bg-slate-800 transition-all ${
            openPanel === 'blue' ? 'overflow-visible border-sky-500/60 shadow-md shadow-sky-900/20' : 'overflow-hidden border-slate-700'
          }`}
        >
          <button
            type="button"
            onClick={() => toggle('blue')}
            className="w-full text-left flex items-center gap-3 py-3 px-4 hover:bg-slate-700/50 transition-colors"
          >
            <div className="p-1.5 bg-sky-900/30 rounded-lg text-sky-400 shrink-0">
              <Cloud size={16} />
            </div>
            <div className="flex flex-col items-start flex-1 min-w-0">
              <span className="font-medium text-sm text-sky-200">Blue Sky</span>
              <span className="text-[10px] text-sky-400/70 leading-none mt-0.5">
                {openPanel === 'blue' ? 'Choose a sky style' : 'Replace exterior sky'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-sky-400/70 shrink-0 transition-transform duration-300 ${openPanel === 'blue' ? 'rotate-180' : ''}`} />
          </button>
          <div className={`sky-fan ${openPanel === 'blue' ? 'sky-fan-open' : ''} ${blueReady ? 'sky-fan-spread' : ''}`}>
            <div className="sky-fan-inner">
              <div className="sky-fan-stage">
                {BLUE_SKY_TYPES.map((type, index) => (
                  <button
                    key={type.id}
                    type="button"
                    className="sky-fan-swatch"
                    style={skyFanVars(index, BLUE_SKY_TYPES.length, { step: 50, arch: 28, maxAngle: 14, delayStep: 45 })}
                    title={type.label}
                    onClick={() => setPaused(true)}
                  >
                    <span className="sky-fan-disc" style={{ background: type.swatch }} />
                    <span className="sky-fan-label">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          data-sky-fan
          className={`sky-fan-sunset rounded-xl border bg-slate-800 transition-all ${
            openPanel === 'sunset' ? 'overflow-visible border-orange-500/60 shadow-md shadow-orange-900/20' : 'overflow-hidden border-slate-700'
          }`}
        >
          <button
            type="button"
            onClick={() => toggle('sunset')}
            className="w-full text-left flex items-center gap-3 py-3 px-4 hover:bg-slate-700/50 transition-colors"
          >
            <div className="p-1.5 bg-orange-900/30 rounded-lg text-orange-400 shrink-0">
              <Sunset size={16} />
            </div>
            <div className="flex flex-col items-start flex-1 min-w-0">
              <span className="font-medium text-sm text-orange-200">Sunset Sky</span>
              <span className="text-[10px] text-orange-400/70 leading-none mt-0.5">
                {openPanel === 'sunset' ? 'Choose a sunset style' : 'Replace with dramatic sunset'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-orange-400/70 shrink-0 transition-transform duration-300 ${openPanel === 'sunset' ? 'rotate-180' : ''}`} />
          </button>
          <div className={`sky-fan ${openPanel === 'sunset' ? 'sky-fan-open' : ''} ${sunsetReady ? 'sky-fan-spread' : ''}`}>
            <div className="sky-fan-inner">
              <div className="sky-fan-stage">
                {SUNSET_TYPES.map((type, index) => (
                  <button
                    key={type.id}
                    type="button"
                    className="sky-fan-swatch"
                    style={skyFanVars(index, SUNSET_TYPES.length, { step: 50, arch: 40, maxAngle: 16, delayStep: 40 })}
                    title={type.label}
                    onClick={() => setPaused(true)}
                  >
                    <span className="sky-fan-disc" style={{ background: type.swatch }} />
                    <span className="sky-fan-label">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[380px] flex flex-col gap-4 mx-auto lg:mx-0">
        <div
          data-botanical-picker
          className={`rounded-xl border bg-slate-800 overflow-hidden transition-all ${
            openPanel === 'flowers' ? 'border-pink-500/60 shadow-md shadow-pink-900/20' : 'border-slate-700'
          }`}
        >
          <button
            type="button"
            onClick={() => toggle('flowers')}
            className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
          >
            <div className="p-1.5 bg-pink-900/30 rounded-lg text-pink-400 shrink-0">
              <Flower2 size={16} />
            </div>
            <div className="flex flex-col items-start flex-1">
              <span className="font-medium text-sm text-pink-200">Add Flowers</span>
              <span className="text-[10px] text-pink-200/70">
                {openPanel === 'flowers' ? 'Choose a type, then tap Go' : 'Auto-place on table'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-pink-400/70 shrink-0 transition-transform duration-300 ${openPanel === 'flowers' ? 'rotate-180' : ''}`} />
          </button>
          <div className={`botanical-panel px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50 ${openPanel === 'flowers' ? 'botanical-panel-open' : ''}`}>
            <div className="flex items-start gap-2 mt-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Flower Type</span>
                  <span className="text-[10px] text-pink-300 truncate">
                    {FLOWER_TYPES.find((type) => type.id === flowerId)?.label}
                  </span>
                </div>
                <BotanicalGrid
                  types={FLOWER_TYPES}
                  selectedId={flowerId}
                  onSelect={(id) => {
                    setPaused(true);
                    setFlowerId(id);
                  }}
                  accent="pink"
                  open={openPanel === 'flowers'}
                />
              </div>
              <div className="flex flex-col shrink-0 gap-2">
                <div>
                  <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</span>
                  <button type="button" className="p-2 rounded-md border bg-slate-900 border-slate-700 text-slate-400" title="Set specific location">
                    <MapPin size={16} />
                  </button>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Go</span>
                  <button type="button" className="p-2 rounded-md border bg-pink-600 border-pink-500 text-white" title="Add flowers">
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          data-botanical-picker
          className={`rounded-xl border bg-slate-800 overflow-hidden transition-all ${
            openPanel === 'plants' ? 'border-emerald-500/60 shadow-md shadow-emerald-900/20' : 'border-slate-700'
          }`}
        >
          <button
            type="button"
            onClick={() => toggle('plants')}
            className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
          >
            <div className="p-1.5 bg-emerald-900/30 rounded-lg text-emerald-400 shrink-0">
              <TreeDeciduous size={16} />
            </div>
            <div className="flex flex-col items-start flex-1">
              <span className="font-medium text-sm text-emerald-200">Add Plant</span>
              <span className="text-[10px] text-emerald-200/70">
                {openPanel === 'plants' ? 'Choose a type, then tap Go' : 'Auto-place in room'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-emerald-400/70 shrink-0 transition-transform duration-300 ${openPanel === 'plants' ? 'rotate-180' : ''}`} />
          </button>
          <div className={`botanical-panel px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50 ${openPanel === 'plants' ? 'botanical-panel-open' : ''}`}>
            <div className="flex items-start gap-2 mt-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Plant Type</span>
                  <span className="text-[10px] text-emerald-300 truncate">
                    {PLANT_TYPES.find((type) => type.id === plantId)?.label}
                  </span>
                </div>
                <BotanicalGrid
                  types={PLANT_TYPES}
                  selectedId={plantId}
                  onSelect={(id) => {
                    setPaused(true);
                    setPlantId(id);
                  }}
                  accent="emerald"
                  open={openPanel === 'plants'}
                />
              </div>
              <div className="flex flex-col shrink-0 gap-2">
                <div>
                  <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</span>
                  <button type="button" className="p-2 rounded-md border bg-slate-900 border-slate-700 text-slate-400" title="Set specific location">
                    <MapPin size={16} />
                  </button>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Go</span>
                  <button type="button" className="p-2 rounded-md border bg-emerald-600 border-emerald-500 text-white" title="Add plant">
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StylePickerShowcase;
